import { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';

interface User {
    id: string;
    email: string;
    fullName: string;
    role: 'ADMIN' | 'MANAGER' | 'USER';
}

interface AuthContextType {
    user: User | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<void>;
    register: (email: string, password: string, fullName: string, role?: string) => Promise<void>;
    logout: () => void;
    accessToken: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
import { API_BASE_URL } from '../config/api';

const API_URL = API_BASE_URL;

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const fetchingRef = useRef(false); // Prevent race conditions
    const initRef = useRef(false); // Prevent double initialization

    useEffect(() => {
        // DEV MODE BYPASS
        const DEV_MODE = true;
        if (DEV_MODE) {
            setUser({
                id: 'dev-user-id',
                email: 'dev@environaise.com',
                fullName: 'Dev Admin',
                role: 'ADMIN'
            });
            setLoading(false);
            return;
        }

        if (initRef.current) return;
        initRef.current = true;

        const storedToken = localStorage.getItem('accessToken');

        if (storedToken) {
            setAccessToken(storedToken);
            fetchCurrentUser(storedToken);
        } else {
            setLoading(false);
        }
    }, []);

    async function fetchCurrentUser(token: string) {
        if (fetchingRef.current) return; // Prevent concurrent fetches
        fetchingRef.current = true;

        try {
            const response = await fetch(API_URL + '/api/auth/me', {
                headers: {
                    Authorization: 'Bearer ' + token,
                },
            });

            if (response.ok) {
                const data: any = await response.json();
                setUser(data.user);
                setLoading(false);
            } else if (response.status === 401) {
                // Token expired, try refresh
                await refreshAccessToken();
            } else {
                // Other error, logout
                logout();
            }
        } catch (error) {
            console.error('Failed to fetch current user:', error);
            logout();
        } finally {
            fetchingRef.current = false;
            setLoading(false);
        }
    }

    async function refreshAccessToken() {
        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
            logout();
            return;
        }

        try {
            const response = await fetch(API_URL + '/api/auth/refresh', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refreshToken }),
            });

            if (response.ok) {
                const data: any = await response.json();
                localStorage.setItem('accessToken', data.accessToken);
                setAccessToken(data.accessToken);

                // Fetch user with new token
                const userResponse = await fetch(API_URL + '/api/auth/me', {
                    headers: {
                        Authorization: 'Bearer ' + data.accessToken,
                    },
                });

                if (userResponse.ok) {
                    const userData: any = await userResponse.json();
                    setUser(userData.user);
                } else {
                    logout();
                }
            } else {
                logout();
            }
        } catch (error) {
            console.error('Failed to refresh token:', error);
            logout();
        }
    }

    async function login(email: string, password: string) {
        setLoading(true);

        try {
            const response = await fetch(API_URL + '/api/auth/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });

            if (!response.ok) {
                const error: any = await response.json();
                throw new Error(error.error || 'Login failed');
            }

            const data: any = await response.json();

            // Store tokens
            localStorage.setItem('accessToken', data.accessToken);
            localStorage.setItem('refreshToken', data.refreshToken);

            // Update state
            setAccessToken(data.accessToken);
            setUser(data.user);
        } finally {
            setLoading(false);
        }
    }

    async function register(
        email: string,
        password: string,
        fullName: string,
        role: string = 'USER'
    ) {
        setLoading(true);

        try {
            const response = await fetch(API_URL + '/api/auth/register', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password, fullName, role }),
            });

            if (!response.ok) {
                const error: any = await response.json();
                throw new Error(error.error || 'Registration failed');
            }

            const data: any = await response.json();

            // Store tokens
            localStorage.setItem('accessToken', data.accessToken);
            localStorage.setItem('refreshToken', data.refreshToken);

            // Update state
            setAccessToken(data.accessToken);
            setUser(data.user);
        } finally {
            setLoading(false);
        }
    }

    function logout() {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        // Also remove legacy 'token' key if it exists
        localStorage.removeItem('token');
        setAccessToken(null);
        setUser(null);
        setLoading(false);
        fetchingRef.current = false;
    }

    const value = { user, loading, login, register, logout, accessToken };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
